/**
 * Tag Card Component - Simplified design with dropdown menu
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MoreHorizontal, ExternalLink, BarChart3, QrCode, 
  Copy, RotateCcw, Trash2, GripVertical, Image, Timer, History, Calendar, ToggleLeft, ToggleRight, Tag 
} from 'lucide-react';

// Color label options
const COLOR_LABELS = [
  { id: 'red', name: 'Red', color: '#ef4444', bg: 'bg-red-500' },
  { id: 'orange', name: 'Orange', color: '#f97316', bg: 'bg-orange-500' },
  { id: 'yellow', name: 'Yellow', color: '#eab308', bg: 'bg-yellow-500' },
  { id: 'green', name: 'Green', color: '#22c55e', bg: 'bg-green-500' },
  { id: 'blue', name: 'Blue', color: '#3b82f6', bg: 'bg-blue-500' },
  { id: 'purple', name: 'Purple', color: '#a855f7', bg: 'bg-purple-500' },
];

export const TagCard = ({
  tag,
  index,
  isSelected,
  isHovered,
  isDragging,
  onSelect,
  onHover,
  onHoverEnd,
  onView,
  onAnalytics,
  onDownloadQR,
  onCopyURL,
  onReset,
  onDelete,
  onSetDates,
  onToggleQR,
  onSetColorLabel,
  onDragStart,
  onDragEnd,
  thumbnailUrl,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRef = useRef(null);
  const navigate = useNavigate();
  
  const hasContent = tag.has_content || tag.message || (tag.media_files && tag.media_files.length > 0);
  
  // Check if tag is expired
  const isExpired = tag.expires_at && new Date(tag.expires_at) < new Date();
  
  // Check if tag is scheduled (goes live in future)
  const isScheduled = tag.goes_live_at && new Date(tag.goes_live_at) > new Date();

  // Update menu position when opened and close on scroll
  useEffect(() => {
    if (menuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const menuHeight = 320; // Approximate menu height
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // If not enough space below, open upward
      if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
        setMenuPosition({
          bottom: window.innerHeight - rect.top + 4,
          right: window.innerWidth - rect.right,
          top: 'auto'
        });
      } else {
        setMenuPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          bottom: 'auto'
        });
      }
      
      // Close menu on scroll
      const handleScroll = () => setMenuOpen(false);
      window.addEventListener('scroll', handleScroll, true);
      
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [menuOpen]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.015 }}
      draggable
      onDragStart={(e) => onDragStart(e, tag)}
      onDragEnd={onDragEnd}
      onMouseEnter={() => onHover(tag.nfc_code)}
      onMouseLeave={onHoverEnd}
      className={`rounded-2xl border bg-card/50 backdrop-blur-xl shadow-sm hover:shadow-lg transition-all cursor-grab active:cursor-grabbing group relative overflow-visible ${
        isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border/40 hover:border-border'
      } ${isDragging ? 'opacity-50 scale-95' : ''}`}
      data-testid={`tag-card-${tag.nfc_code}`}
    >
      {/* Thumbnail Preview on Hover */}
      {isHovered && thumbnailUrl && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full z-50 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-card rounded-xl shadow-2xl border border-border overflow-hidden"
          >
            <img 
              src={thumbnailUrl} 
              alt="Preview" 
              className="w-40 h-40 object-cover"
            />
          </motion.div>
          <div className="w-3 h-3 bg-card border-l border-b border-border rotate-45 absolute -bottom-1.5 left-1/2 -translate-x-1/2" />
        </div>
      )}

      {/* Card Content */}
      <div className="p-4">
        {/* Top Row: Checkbox, Status, Menu */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onSelect(tag.nfc_code)}
              className="w-4 h-4 rounded border-border"
              onClick={(e) => e.stopPropagation()}
            />
            {/* Color Label */}
            {tag.color_label && (
              <div 
                className={`w-3 h-3 rounded-full`}
                style={{ backgroundColor: COLOR_LABELS.find(c => c.id === tag.color_label)?.color || '#gray' }}
                title={`Label: ${tag.color_label}`}
              />
            )}
            {/* Status Indicator */}
            <div 
              className={`w-2 h-2 rounded-full ${
                isExpired ? 'bg-orange-500' : isScheduled ? 'bg-blue-500' : hasContent ? 'bg-green-500' : 'bg-gray-300'
              }`} 
              title={isExpired ? 'Expired' : isScheduled ? 'Scheduled' : hasContent ? 'Has content' : 'Empty'} 
            />
            {/* Expired Badge */}
            {isExpired && (
              <span className="flex items-center gap-1 text-xs text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">
                <Timer className="w-3 h-3" />
                Expired
              </span>
            )}
            {/* Scheduled Badge */}
            {isScheduled && !isExpired && (
              <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
                <Calendar className="w-3 h-3" />
                Scheduled
              </span>
            )}
            {/* QR Disabled Badge */}
            {tag.qr_enabled === false && (
              <span className="flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded-full" title="QR code disabled for this tag">
                <QrCode className="w-3 h-3" />
                No QR
              </span>
            )}
          </div>
          
          {/* Dropdown Menu */}
          <div className="relative">
            <button
              ref={menuButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen(!menuOpen);
              }}
              className="p-1.5 hover:bg-secondary rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              data-testid={`menu-btn-${tag.nfc_code}`}
            >
              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
            </button>
            
            {menuOpen && createPortal(
              <>
                <div className="fixed inset-0 z-[100]" onClick={() => setMenuOpen(false)} />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="fixed z-[101] bg-card border border-border rounded-xl shadow-xl py-1 min-w-[160px]"
                  style={{ 
                    top: menuPosition.top !== 'auto' ? menuPosition.top : undefined,
                    bottom: menuPosition.bottom !== 'auto' ? menuPosition.bottom : undefined,
                    right: menuPosition.right 
                  }}
                >
                  <button
                    onClick={() => { onView(); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" /> View Tag
                  </button>
                  <button
                    onClick={() => { onAnalytics(); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                  >
                    <BarChart3 className="w-4 h-4" /> Analytics
                  </button>
                  <button
                    onClick={() => { navigate(`/admin/history/${tag.nfc_code}`); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                  >
                    <History className="w-4 h-4" /> View Logs
                  </button>
                  <button
                    onClick={() => { onDownloadQR(); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                  >
                    <QrCode className="w-4 h-4" /> Download QR
                  </button>
                  <button
                    onClick={() => { onCopyURL(); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" /> Copy URL
                  </button>
                  <div className="border-t border-border my-1" />
                  {/* Color Label Picker */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowColorPicker(!showColorPicker);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                    >
                      <Tag className="w-4 h-4" /> 
                      <span>Color Label</span>
                      {tag.color_label && (
                        <div 
                          className="w-3 h-3 rounded-full ml-auto"
                          style={{ backgroundColor: COLOR_LABELS.find(c => c.id === tag.color_label)?.color }}
                        />
                      )}
                    </button>
                    {showColorPicker && (
                      <div className="px-3 py-2 flex flex-wrap gap-2">
                        {COLOR_LABELS.map(color => (
                          <button
                            key={color.id}
                            onClick={() => {
                              onSetColorLabel && onSetColorLabel(tag.nfc_code, color.id);
                              setShowColorPicker(false);
                              setMenuOpen(false);
                            }}
                            className={`w-6 h-6 rounded-full hover:scale-110 transition-transform ${
                              tag.color_label === color.id ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                            }`}
                            style={{ backgroundColor: color.color }}
                            title={color.name}
                          />
                        ))}
                        <button
                          onClick={() => {
                            onSetColorLabel && onSetColorLabel(tag.nfc_code, null);
                            setShowColorPicker(false);
                            setMenuOpen(false);
                          }}
                          className={`w-6 h-6 rounded-full border-2 border-dashed border-gray-300 hover:scale-110 transition-transform ${
                            !tag.color_label ? 'ring-2 ring-offset-2 ring-gray-400' : ''
                          }`}
                          title="No label"
                        />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { onSetDates && onSetDates(tag); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 text-blue-600"
                    data-testid={`set-dates-btn-${tag.nfc_code}`}
                  >
                    <Calendar className="w-4 h-4" /> Set Dates
                  </button>
                  <button
                    onClick={() => { onToggleQR && onToggleQR(tag); setMenuOpen(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 ${
                      tag.qr_enabled === false ? 'text-orange-600' : 'text-green-600'
                    }`}
                    data-testid={`toggle-qr-btn-${tag.nfc_code}`}
                  >
                    {tag.qr_enabled === false ? (
                      <>
                        <ToggleLeft className="w-4 h-4" /> Enable QR Code
                      </>
                    ) : (
                      <>
                        <ToggleRight className="w-4 h-4" /> Disable QR Code
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => { onReset(); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-orange-50 text-orange-600 flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" /> Reset
                  </button>
                  <button
                    onClick={() => { onDelete(); setMenuOpen(false); }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-destructive/10 text-destructive flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </motion.div>
              </>,
              document.body
            )}
          </div>
        </div>
        
        {/* Title & Code */}
        <h3 className="font-semibold text-sm mb-1 truncate">{tag.description || 'Untitled'}</h3>
        <code className="text-xs text-muted-foreground font-mono block truncate mb-2">{tag.nfc_code}</code>
        
        {/* Meta Info Row */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {tag.batch && (
              <span className="bg-secondary/80 px-2 py-0.5 rounded-full truncate max-w-[80px]">{tag.batch}</span>
            )}
            {tag.media_files?.length > 0 && (
              <span className="flex items-center gap-1">
                <Image className="w-3 h-3" />
                {tag.media_files.length}
              </span>
            )}
          </div>
          <span>{new Date(tag.created_at).toLocaleDateString()}</span>
        </div>

        {/* View Button */}
        <a
          href={`/${tag.nfc_code}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mt-3 w-full bg-primary text-primary-foreground rounded-lg px-3 py-2 text-xs font-medium text-center hover:opacity-90 transition-all flex items-center justify-center gap-1.5"
        >
          <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
          View
        </a>
      </div>
      
      {/* Drag handle indicator */}
      <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <GripVertical className="w-3 h-3 text-muted-foreground" />
      </div>
    </motion.div>
  );
};

export default TagCard;
